import { Check, Leaf, ShieldCheck, Truck, Wallet, ShoppingBasket } from "lucide-react";
import { Link } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import TrustBadges from "../components/TrustBadges";
import EducationSection from "../components/EducationSection";
import FaqSection, { HOME_FAQS } from "../components/FaqSection";
import { Button } from "../components/ui/button";
import { useApp } from "../context/AppContext";
import SEOHead from "../components/SEOHead";
import PublicBreadcrumbs from "../components/PublicBreadcrumbs";
import {
  buildBreadcrumbSchema,
  buildCollectionPageSchema,
  buildFaqSchema,
  buildOrganizationSchema,
  buildWebsiteSchema,
} from "../lib/seo";

const features = [
  { icon: Leaf, title: "100% Herbal Pilihan", desc: "Diolah dari bahan alami terbaik." },
  { icon: ShieldCheck, title: "Aman untuk Keluarga", desc: "Bebas pengawet berbahaya." },
  { icon: Truck, title: "Pengiriman Cepat", desc: "Tiba dengan kurir terpercaya." },
  { icon: Wallet, title: "Pembayaran Mudah", desc: "Transfer, QRIS, atau COD." },
];

const brandHighlights = [
  "Produk herbal dan alami pilihan",
  "Praktis untuk kebutuhan harian",
  "Mendukung gaya hidup sehat",
  "Pelayanan responsif",
  "Belanja mudah dan nyaman",
];

const homeMissionItems = [
  "Menyediakan produk herbal dan alami yang berkualitas.",
  "Menghadirkan pengalaman belanja yang mudah dan nyaman.",
  "Memberikan informasi produk yang jelas dan edukatif.",
  "Mendukung masyarakat dalam menjalani pola hidup sehat.",
  "Mengutamakan pelayanan yang responsif dan terpercaya.",
];

const slugifyCategory = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-");

const LandingPage = () => {
  const { products, categories, cartCount, openCart, loading } = useApp();
  const activeProducts = products.filter((p) => (p.is_active ?? p.active ?? true) !== false);

  const apiCategoryNames = Array.isArray(categories)
    ? categories.map((cat) => cat?.name).filter(Boolean)
    : [];
  const productCategoryNames = activeProducts.map((p) => p.category).filter(Boolean);
  const categoryNames = Array.from(new Set([...apiCategoryNames, ...productCategoryNames]));
  const categorySections = categoryNames
    .map((cat) => ({
      name: cat,
      products: activeProducts.filter((p) => p.category === cat),
    }))
    .filter((section) => Array.isArray(section.products) && section.products.length > 0);

  const breadcrumbItems = [
    { name: "Beranda", path: "/" },
  ];

  const pageSchema = [
    buildOrganizationSchema(),
    buildWebsiteSchema(),
    buildBreadcrumbSchema(breadcrumbItems),
    buildFaqSchema(HOME_FAQS),
    buildCollectionPageSchema({
      name: "Produk Herbal Alami Teras Alami",
      description:
        "Koleksi produk herbal alami Teras Alami untuk teh rosella, teh bunga telang, minuman hangat, dan wellness keluarga.",
      path: "/",
      products: activeProducts,
    }),
  ];

  return (
    <main className="pb-28 md:pb-0">
      <SEOHead
        title="Teras Alami - Produk Herbal & Alami untuk Gaya Hidup Sehat"
        description="Teras Alami menyediakan produk herbal dan alami pilihan untuk mendukung gaya hidup sehat keluarga Indonesia dengan layanan yang mudah, nyaman, dan terpercaya."
        path="/"
        schema={pageSchema}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-grain" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-28 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <PublicBreadcrumbs items={breadcrumbItems} />
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-sm font-medium border border-emerald-100">
              <Leaf className="w-4 h-4" /> Segar langsung dari kebun herbal
            </span>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-stone-900 leading-tight">
              Teras Alami untuk{" "}
              <span className="text-emerald-700">Kesehatan Keluarga</span>
            </h1>
            <p className="mt-5 text-lg text-stone-600 leading-relaxed max-w-xl">
              Teras Alami menghadirkan produk herbal alami, teh rosella, minuman herbal, dan
              pilihan wellness keluarga dengan rasa nyaman, tampilan premium, dan proses belanja
              yang mudah diakses dari mobile.
            </p>
            <p className="mt-4 max-w-2xl rounded-2xl border border-emerald-100 bg-white/85 px-5 py-4 text-sm leading-7 text-stone-600">
              Ringkasan halaman: brand herbal Indonesia ini fokus pada produk herbal alami untuk
              keluarga, termasuk teh rosella, bunga telang, minuman hangat, dan edukasi konsumsi
              herbal yang wajar tanpa klaim medis berlebihan.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#produk"
                className="inline-flex items-center justify-center h-14 px-6 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-lg font-medium transition hover:-translate-y-0.5"
                data-testid="cta-pilih-produk"
              >
                Pilih Produk Sekarang
              </a>
              {cartCount > 0 && (
                <Button
                  type="button"
                  onClick={openCart}
                  variant="outline"
                  className="h-14 px-6 rounded-xl text-lg border-stone-200"
                  data-testid="cta-lihat-keranjang"
                >
                  <ShoppingBasket className="w-5 h-5 mr-2" /> Lihat Keranjang ({cartCount})
                </Button>
              )}
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-6 bg-emerald-100/60 rounded-[3rem] blur-2xl"></div>
            <div className="relative rounded-[2rem] overflow-hidden border border-stone-200 shadow-sm aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] bg-stone-100">
              <img
                src="https://images.pexels.com/photos/10802278/pexels-photo-10802278.jpeg"
                alt="Minuman herbal alami Teras Alami dengan nuansa premium dan healthy lifestyle"
                className="w-full h-full object-cover"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </div>
            <div className="hidden sm:flex absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-lg border border-stone-100 px-5 py-4 items-center gap-3">
              <span className="w-10 h-10 rounded-xl bg-rose-100 text-rose-700 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5" />
              </span>
              <div>
                <p className="text-sm text-stone-500">Dipercaya sejak</p>
                <p className="font-display text-lg font-semibold text-stone-900">
                  1.000+ keluarga
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Features */}
      <section aria-labelledby="brand-features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
        <h2 id="brand-features" className="sr-only">
          Keunggulan brand Teras Alami
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {features.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                className="bg-white rounded-2xl border border-stone-200 p-5 sm:p-6 shadow-sm"
                data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <span className="inline-flex w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 items-center justify-center mb-3">
                  <Icon className="w-5 h-5" />
                </span>
                <p className="font-display text-lg font-semibold text-stone-900">
                  {f.title}
                </p>
                <p className="text-stone-600 text-sm mt-1">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      <TrustBadges />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-sm sm:p-8">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-sm font-medium border border-emerald-100">
              <Leaf className="w-4 h-4" /> Tentang Teras Alami
            </span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold text-stone-900 leading-tight">
              Teras Alami, Pilihan Natural untuk Gaya Hidup Sehat
            </h2>
            <p className="mt-4 text-stone-600 text-base sm:text-lg leading-relaxed">
              Teras Alami menyediakan berbagai produk herbal, alami, dan kebutuhan kesehatan harian
              yang dipilih untuk mendukung gaya hidup sehat keluarga. Produk kami dapat menjadi
              bagian dari pola hidup seimbang, dengan tetap mengutamakan kualitas, kenyamanan, dan
              kepercayaan pelanggan.
            </p>
          </div>

          <div className="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-sm sm:p-8">
            <h3 className="font-display text-2xl font-semibold text-stone-900">
              Keunggulan Kami
            </h3>
            <ul className="mt-5 space-y-3">
              {brandHighlights.map((item) => (
                <li key={item} className="flex items-start gap-3 text-stone-700">
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-stone-50 border-y border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-[2rem] border border-emerald-100 bg-emerald-50 p-6 shadow-sm sm:p-8">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold text-stone-900">
                Visi
              </h2>
              <p className="mt-4 text-stone-700 text-base sm:text-lg leading-relaxed">
                Menjadi brand terpercaya dalam menyediakan produk herbal dan alami yang mendukung
                gaya hidup sehat masyarakat Indonesia.
              </p>
            </div>
            <div className="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-sm sm:p-8">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold text-stone-900">
                Misi
              </h2>
              <ul className="mt-5 space-y-3">
                {homeMissionItems.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-stone-700">
                    <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                      <Check className="h-3.5 w-3.5" />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section id="produk" aria-labelledby="produk-heading" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <h2 id="produk-heading" className="font-display text-3xl sm:text-4xl font-semibold text-stone-900">
              Produk Herbal Pilihan
            </h2>
            <p className="text-stone-600 mt-2 text-lg">
              Minuman herbal, teh rosella, dan suplemen alami dengan positioning premium herbal
              yang tetap ramah untuk kebutuhan keluarga sehari-hari.
            </p>
          </div>
          <Link
            to="/telang-rosella"
            className="inline-flex items-center rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700 hover:bg-emerald-100"
          >
            Jelajahi landing Teh Rosella & Bunga Telang
          </Link>
        </div>

        {categorySections.length > 1 && (
          <div className="flex gap-2 mb-6 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="category-pills">
            {categorySections.map(({ name }) => (
              <a
                key={name}
                href={`#cat-${slugifyCategory(name)}`}
                className="whitespace-nowrap text-sm font-medium px-4 py-2 rounded-full bg-white border border-stone-200 hover:border-emerald-300 hover:text-emerald-700 transition"
              >
                {name}
              </a>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
            <p className="mt-4 text-stone-600">Memuat produk...</p>
          </div>
        ) : activeProducts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-stone-600 text-lg">Belum ada produk tersedia.</p>
          </div>
        ) : (
          <div className="space-y-12">
            {categorySections.map(({ name, products: catProducts }) => {
              if (!catProducts || catProducts.length === 0) {
                return null;
              }

              return (
                <div key={name} id={`cat-${slugifyCategory(name)}`}>
                  <h3 className="font-display text-xl sm:text-2xl font-semibold text-stone-900 mb-4">
                    {name}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
                    {catProducts.map((p) => (
                      <ProductCard key={p.id} product={p} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
        <div className="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-sm sm:p-8">
          <h2 className="font-display text-2xl font-semibold text-stone-900">
            Informasi penting sebelum membeli produk herbal
          </h2>
          <ul className="mt-4 space-y-3 text-sm leading-7 text-stone-600">
            <li>Produk Teras Alami ditujukan sebagai minuman herbal alami dan pendamping gaya hidup sehat.</li>
            <li>Deskripsi manfaat disusun secara natural untuk membantu Anda memahami karakter produk, bukan pengganti nasihat medis.</li>
            <li>Untuk kondisi kesehatan tertentu, konsumsi secara wajar dan konsultasikan dengan tenaga kesehatan bila diperlukan.</li>
          </ul>
        </div>
      </section>

      <EducationSection />
      <FaqSection />
    </main>
  );
};

export default LandingPage;
